import { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { useSession } from "next-auth/react";
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
  MeasuringStrategy,
  pointerWithin,
  rectIntersection,
  getFirstCollision,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  Box,
  Button,
  Flex,
  Grid,
  Input,
  VStack,
  useToast,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import * as analytics from 'utils/analytics';

// Kanban components
import { Column } from './column';
import { Item } from './item';
import ColumnModal from './column-modal';
import ColumnManager from './column-manager';
import ItemDragOverlay from './assets/item-drag-overlay';
import ScheduleModal from './schedule-modal';
import { colors, NO_STATUS_COLUMN, SCHEDULED_COLUMN } from './constants';
import { DNDType, Placeholder, PendingScheduleItem } from './types';
import EmptyScheduleIcon from './assets/empty-schedule-icon';

// Utils and hooks
import { getAccount } from "../../utils/sessionHelper";
import { useFirebaseUser } from 'utils/useFirebaseUser';
import { TweetContext } from 'context/tweetContext';

// Controllers
import { deleteKanbanPost, saveKanbanData, subscribeToKanbanData, subscribeToKanbanDrafts, setDefaultColumns } from "../../controllers/kanbanController";
import { scheduleTweet, unschedule } from 'controllers/schedule';

// Icons
import { LuPlus, LuSearch } from 'react-icons/lu';
import { AddIcon } from '@chakra-ui/icons';

export function KanbanBoard() {
  const { data: session } = useSession();
  const isAuthenticated = useFirebaseUser();
  const [columns, setColumns] = useState<DNDType[]>([]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [columnName, setColumnName] = useState('');
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [columnColor, setColumnColor] = useState('grey');
  const toast = useToast();
  const [draftsMap, setDraftsMap] = useState<Record<string, any>>({});
  const [scheduledDraftsMap, setScheduledDraftsMap] = useState<Record<string, any>>({});
  const [editingColumn, setEditingColumn] = useState<DNDType | null>(null);
  const account = getAccount(session);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [placeholder, setPlaceholder] = useState<Placeholder | null>(null);
  const [draggedItemHeight, setDraggedItemHeight] = useState<number>(0);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [pendingScheduleItem, setPendingScheduleItem] = useState<PendingScheduleItem | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const { isOpen: isDeleteModalOpen, onOpen: openDeleteModal, onClose: closeDeleteModal } = useDisclosure();
  const [pendingDeleteColumnId, setPendingDeleteColumnId] = useState<string | null>(null);
  const {
    isOpen: isDeleteColumnModalOpen,
    onOpen: openDeleteColumnModal,
    onClose: closeDeleteColumnModal
  } = useDisclosure();

  const placeholderTextColor = useColorModeValue(
    colors.light.blue.placeholderText,
    colors.dark.blue.placeholderText
  );
  

  const tweetContext: any = useContext(TweetContext);

  const lastOverId = useRef<UniqueIdentifier | null>(null);

  const customCollisionDetection = useCallback((args: any) => {
    // First try pointer intersections
    const pointerIntersections = pointerWithin(args);

    // If no pointer intersections, fall back to rect intersection
    const intersections = pointerIntersections.length > 0
      ? pointerIntersections
      : rectIntersection(args);

    let overId = getFirstCollision(intersections, 'id');

    if (overId) {
      // Find the column that contains this item/is this item
      const overColumn = columns.find(col =>
        col.id === overId || col.items.includes(overId as string)
      );

      if (overColumn) {
        // If we're over a column or column item, store this as last valid collision
        lastOverId.current = overId;
        return [{ id: overId }];
      }
    }

    // If no valid collision found, use last known good collision
    return lastOverId.current ? [{ id: lastOverId.current }] : [];
  }, [columns]);

  useEffect(() => {
    if (!session || !isAuthenticated) return;

    // Set first access flag
    setDefaultColumns(session);

    const unsubscribeKanban = subscribeToKanbanData(
      session,
      (storedColumns) => {
        const visibilityState = storedColumns.reduce((acc, col) => ({
          ...acc,
          [col.id]: col.isVisible ?? true
        }), {});
        setColumnVisibility(visibilityState);

        const columnsData = storedColumns;

        const assignedDrafts = new Set<string>();
        columnsData.forEach(col => {
          col.items.forEach(itemId => {
            assignedDrafts.add(itemId);
          });
        });

        setColumns(prev => {
          const noStatusCol = prev.find(col => col.id === NO_STATUS_COLUMN.id);
          const scheduledCol = prev.find(col => col.id === SCHEDULED_COLUMN.id);
          return [
            noStatusCol || NO_STATUS_COLUMN,
            ...columnsData,
            scheduledCol || SCHEDULED_COLUMN
          ];
        });
      },
      (error) => {
        console.error('Error in kanban subscription:', error);
        toast({
          title: 'Error',
          description: 'Failed to load kanban data',
          status: 'error',
        });
      }
    );

    const unsubscribeDrafts = subscribeToKanbanDrafts(
      session,
      "draft",
      (drafts) => {
        const newDraftsMap = drafts.reduce((acc, draft) => {
          acc[draft.id] = draft;
          return acc;
        }, {} as Record<string, any>);
        setDraftsMap(newDraftsMap);

        setColumns(prev => {
          const assignedDrafts = new Set<string>();
          prev.forEach(col => {
            if (col.id !== NO_STATUS_COLUMN.id) {
              col.items.forEach(itemId => {
                assignedDrafts.add(itemId);
              });
            }
          });

          const noStatusItems = drafts
            .filter(draft => !assignedDrafts.has(draft.id))
            .map(draft => draft.id);

          return prev.map(col =>
            col.id === NO_STATUS_COLUMN.id
              ? { ...col, items: noStatusItems }
              : col
          );
        });
      }
    );

    const unsubscribeScheduled = subscribeToKanbanDrafts(
      session,
      "scheduled",
      (scheduledPosts) => {
        const newScheduledMap = scheduledPosts.reduce((acc, post) => {
          acc[post.id] = post;
          return acc;
        }, {} as Record<string, any>);
        setScheduledDraftsMap(newScheduledMap);

        setColumns(prev => prev.map(col =>
          col.id === SCHEDULED_COLUMN.id
            ? { ...col, items: scheduledPosts.map(post => post.id) }
            : col
        ));
      }
    );

    return () => {
      unsubscribeKanban();
      unsubscribeDrafts();
      unsubscribeScheduled();
    };
  }, [session, isAuthenticated, toast]);

  const saveAndRefreshKanban = useCallback(async (newColumns: DNDType[]) => {
    if (!session) return;

    try {
      const columnsToSave = newColumns.filter(
        col => col.id !== NO_STATUS_COLUMN.id && col.id !== SCHEDULED_COLUMN.id
      );
      await saveKanbanData(session, columnsToSave);
      analytics.log('kanban_data_saved');
    } catch (error) {
      console.error('Failed to save kanban updates:', error);
      toast({
        title: 'Error',
        description: 'Failed to save changes',
        status: 'error',
      });
    }
  }, [session, toast]);

  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return columns;

    return columns.map(column => ({
      ...column,
      items: column.items.filter(itemId => {
        const draft = draftsMap[itemId];
        return draft?.text?.toLowerCase().includes(searchQuery.toLowerCase());
      })
    }));
  }, [columns, draftsMap, searchQuery]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 15,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onAddColumn = useCallback(async () => {
    if (!columnName || !session?.user?.uid) return;

    const newColumns = [...columns];

    if (editingColumn) {
      const columnIndex = newColumns.findIndex(col => col.id === editingColumn.id);
      if (columnIndex !== -1) {
        newColumns[columnIndex] = {
          ...newColumns[columnIndex],
          title: columnName,
          color: columnColor,
        };
      }
    } else {
      newColumns.push({
        id: uuidv4(),
        title: columnName,
        color: columnColor,
        items: [],
      });
    }

    await saveAndRefreshKanban(newColumns);
    analytics.log('kanban_added_column');
    setColumnName('');
    setColumnColor('grey');
    setShowAddColumnModal(false);
    setEditingColumn(null);
  }, [columnName, columnColor, columns, session, editingColumn, saveAndRefreshKanban]);

  const handleEditColumn = useCallback((column: DNDType) => {
    setEditingColumn(column);
    setColumnName(column.title);
    setColumnColor(column.color);
    setShowAddColumnModal(true);
  }, []);

  const handleDeleteColumn = useCallback((columnId: string) => {
    setPendingDeleteColumnId(columnId);
    openDeleteColumnModal();
  }, []);

  const handleDeleteColumnConfirm = useCallback(async () => {
    if (!session?.user?.uid || !pendingDeleteColumnId) return;

    const columnToDelete = columns.find(col => col.id === pendingDeleteColumnId);
    if (!columnToDelete) return;

    const newColumns = columns.map(col => {
      if (col.id === NO_STATUS_COLUMN.id) {
        return {
          ...col,
          items: [...col.items, ...columnToDelete.items]
        };
      }
      return col;
    }).filter(col => col.id !== pendingDeleteColumnId);

    setColumns(newColumns);
    await saveAndRefreshKanban(newColumns);
    analytics.log('kanban_column_deleted');
    closeDeleteColumnModal();
    setPendingDeleteColumnId(null);
  }, [columns, session, saveAndRefreshKanban, pendingDeleteColumnId]);

  const handleCloseModal = useCallback(() => {
    setShowAddColumnModal(false);
    setEditingColumn(null);
    setColumnName('');
    setColumnColor('grey');
  }, []);

  const handleToggleVisibility = async (columnId: string) => {
    const newVisibility = {
      ...columnVisibility,
      [columnId]: !columnVisibility[columnId]
    };
    setColumnVisibility(newVisibility);

    const newColumns = columns.map(col => ({
      ...col,
      isVisible: newVisibility[col.id]
    }));

    await saveAndRefreshKanban(newColumns);
  };

  const handleDropdownDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    const oldIndex = columns.findIndex(col => col.id === active.id);
    const newIndex = columns.findIndex(col => col.id === over.id);

    const newColumns = arrayMove(columns, oldIndex, newIndex);
    setColumns(newColumns);
    await saveAndRefreshKanban(newColumns);
  }, [columns, saveAndRefreshKanban]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id);

    const element = document.querySelector(`[data-id="${active.id}"]`);
    if (element) {
      setDraggedItemHeight(element.getBoundingClientRect().height);
    }
  }, []);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { active, over } = event;
    if (!over) {
      setPlaceholder(null);
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeColumn = columns.find(col => col.items.includes(activeId));
    const overColumn = columns.find(col => col.items.includes(overId) || col.id === overId);

    if (!activeColumn || !overColumn) {
      setPlaceholder(null);
      return;
    }


    const overItemIndex = overColumn.items.indexOf(overId);
    setPlaceholder({
      columnId: overColumn.id,
      index: overItemIndex === -1 ? overColumn.items.length : overItemIndex
    });
  }, [columns]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setPlaceholder(null);
    setDraggedItemHeight(0);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    const activeColumn = columns.find(col => col.id === activeId);
    const isColumnDrag = !!activeColumn;

    if (isColumnDrag) {
      if (
        activeId === NO_STATUS_COLUMN.id ||
        activeId === SCHEDULED_COLUMN.id ||
        overId === NO_STATUS_COLUMN.id ||
        overId === SCHEDULED_COLUMN.id
      ) {
        setActiveId(null);
        return;
      }

      const oldIndex = columns.findIndex(col => col.id === activeId);
      const newIndex = columns.findIndex(col => col.id === overId);

      const newColumns = arrayMove(columns, oldIndex, newIndex);
      setColumns(newColumns);

      try {
        const columnsToSave = newColumns.filter(
          col => col.id !== NO_STATUS_COLUMN.id && col.id !== SCHEDULED_COLUMN.id
        );
        await saveAndRefreshKanban(columnsToSave);
      } catch (error) {
        console.error('Failed to save column order:', error);
      }
    } else {


      const activeColumn = columns.find(col => col.items.includes(activeId));
      const overColumn = columns.find(col => col.items.includes(overId) || col.id === overId);
      if (!activeColumn || !overColumn) return;

      if (overColumn.id === SCHEDULED_COLUMN.id && activeColumn.id !== SCHEDULED_COLUMN.id) {
        setPendingScheduleItem({
          itemId: activeId,
          sourceColumnId: activeColumn.id
        });
        setShowScheduleModal(true);
        setActiveId(null);
        return;
      }

      if (activeColumn.id === SCHEDULED_COLUMN.id && overColumn.id !== SCHEDULED_COLUMN.id) {
        await unschedule(session, scheduledDraftsMap[activeId], tweetContext);
      }
      const activeColumnIndex = columns.findIndex(col => col.id === activeColumn.id);
      const overColumnIndex = columns.findIndex(col => col.id === overColumn.id);

      const activeItemIndex = activeColumn.items.indexOf(activeId);
      let newOverItemIndex;

      if (activeColumn.id === overColumn.id) {
        newOverItemIndex = overColumn.items.indexOf(overId);
      } else {
        newOverItemIndex = overId === overColumn.id
          ? overColumn.items.length
          : overColumn.items.indexOf(overId);
      }

      let newColumns = [...columns];

      if (activeColumn.id === overColumn.id) {
        const newItems = arrayMove(
          activeColumn.items,
          activeItemIndex,
          newOverItemIndex
        );
        newColumns[activeColumnIndex] = {
          ...activeColumn,
          items: newItems,
        };
      } else {
        const newActiveItems = [...activeColumn.items];
        const newOverItems = [...overColumn.items];
        newActiveItems.splice(activeItemIndex, 1);
        newOverItems.splice(newOverItemIndex, 0, activeId);

        newColumns[activeColumnIndex] = {
          ...activeColumn,
          items: newActiveItems,
        };
        newColumns[overColumnIndex] = {
          ...overColumn,
          items: newOverItems,
        };
      }

      setColumns(newColumns);
      try {
        const columnsToSave = newColumns.filter(
          col => col.id !== NO_STATUS_COLUMN.id && col.id !== SCHEDULED_COLUMN.id
        );
        await saveAndRefreshKanban(columnsToSave);
      } catch (error) {
        console.error('Failed to save column updates:', error);
        toast({
          title: 'Error',
          description: 'Failed to save changes',
          status: 'error',
        });
      }
    }
    setActiveId(null);
    analytics.log('kanban_item_moved');
  }, [columns, session, toast, draftsMap, saveAndRefreshKanban]);

  const getDraggedItem = useCallback((id: UniqueIdentifier | null) => {
    if (!id) return null;

    return draftsMap[id] || scheduledDraftsMap[id] || null;
  }, [draftsMap, scheduledDraftsMap]);

  const getDraggedColumn = useCallback((id: UniqueIdentifier | null) => {
    if (!id) return null;
    return columns.find(col => col.id === id) || null;
  }, [columns]);

  const handleSchedule = async (date: Date) => {
    if (!pendingScheduleItem || !session) return;

    const { itemId, sourceColumnId } = pendingScheduleItem;

    try {
      const account = getAccount(session);
      const scheduledData = {
        idUser: account?.id,
        idAccount: account?.idAccount,
        twUserName: account?.twUserName,
        id: itemId,
        text: draftsMap[itemId]?.text,
        isScheduled: true,
        scheduledTime: date.toISOString(),
        isAutoPlug: false,
        isAutoRetweet: account?.isAutoRetweet ?? false,
        autoRetweetHours: account?.autoRetweetHours ?? 12,
        autoRetweetTimes: account?.autoRetweetTimes ?? 1,
        isPreventLinkExpand: account?.isPreventLinkExpand ?? 1,
        isPreventLinkExpandAutoPlug: account?.isPreventLinkExpand ?? 1,
        isRetweeted: false,
        isPlugged: false,
        autoRtWithOtherAccounts: account?.autoRtWithOtherAccounts ?? false,
      };

      await scheduleTweet(
        undefined,
        undefined,
        scheduledData,
        account?.timezone,
        tweetContext
      );
      const updatedColumns = [...columns];
      const sourceColumn = updatedColumns.find(col => col.id === sourceColumnId);
      if (sourceColumn) {
        sourceColumn.items = sourceColumn.items.filter(id => id !== itemId);
      }
      await saveAndRefreshKanban(
        updatedColumns.filter(col =>
          col.id !== NO_STATUS_COLUMN.id &&
          col.id !== SCHEDULED_COLUMN.id
        )
      );

    } catch (error) {
      console.error('Failed to schedule tweet:', error);
      toast({
        title: 'Error',
        description: 'Failed to schedule tweet',
        status: 'error',
      });
    } finally {
      setShowScheduleModal(false);
      setPendingScheduleItem(null);
      analytics.log('kanban_scheduled_post');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId || !session) return;

    try {
      await deleteKanbanPost(session, pendingDeleteId);
      closeDeleteModal();
      setPendingDeleteId(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete post',
        status: 'error',
      });
    }
  };
  return (
    <Box>
      <ColumnModal
        isOpen={showAddColumnModal}
        onClose={handleCloseModal}
        columnName={columnName}
        columnColor={columnColor}
        setColumnName={setColumnName}
        setColumnColor={setColumnColor}
        onAddColumn={onAddColumn}
        isEditing={!!editingColumn}
      />
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setPendingScheduleItem(null);
        }}
        onSchedule={handleSchedule}
      />
      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalBody>
            Are you sure you want to delete this post?
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button variant="primaryDestructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={isDeleteColumnModalOpen} onClose={closeDeleteColumnModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Column</ModalHeader>
          <ModalBody>
            Are you sure you want to delete this column? All items will be moved to Ideas.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeDeleteColumnModal}>
              Cancel
            </Button>
            <Button variant="primaryDestructive" onClick={handleDeleteColumnConfirm}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Flex gap={4} direction="column">
        <Flex justify="space-between">
          <Flex align="center" gap={2}>
            <ColumnManager
              columns={filteredColumns}
              columnVisibility={columnVisibility}
              fixedColumns={[NO_STATUS_COLUMN.id, SCHEDULED_COLUMN.id]}
              sensors={sensors}
              onDragEnd={handleDropdownDragEnd}
              onToggleVisibility={handleToggleVisibility}
              onEditColumn={handleEditColumn}
              onDeleteColumn={handleDeleteColumn}
              onAddColumn={() => setShowAddColumnModal(true)}
            />
            <Box>
              <InputGroup
                flex="1"
              >
                <InputLeftElement pointerEvents='none' color="gray.500">
                  <LuSearch />
                </InputLeftElement>
                <Input placeholder="Search " value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)} />
              </InputGroup>
            </Box>

          </Flex>
          <Box>
            <Button
              variant="secondary"
              width="100%"
              leftIcon={<AddIcon />}
              onClick={() => setShowAddColumnModal(true)}
            >
              Add column
            </Button>
          </Box>
        </Flex>
        <Box>
          <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.Always,
              },
            }}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={filteredColumns.map((i) => i.id)}>
              <Grid gap={6} gridAutoFlow="column" gridTemplateColumns="repeat(auto-fill, 438px)" overflow="auto" sx={{ '::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                {filteredColumns
                  .filter(column => {
                    const isFixedColumn = column.id === NO_STATUS_COLUMN.id || column.id === SCHEDULED_COLUMN.id;
                    return isFixedColumn || columnVisibility[column.id];
                  })
                  .map((column) => (
                    <Column
                      id={column.id}
                      title={column.title}
                      key={column.id}
                      color={column.color}
                      isDraggable={column.id !== NO_STATUS_COLUMN.id && column.id !== SCHEDULED_COLUMN.id}
                      hasNewButton={column.id === NO_STATUS_COLUMN.id}
                      onAddItem={column.id !== SCHEDULED_COLUMN.id ? async () => {
                        const postId = uuidv4();
                        tweetContext.newTweet({ text: "" }, postId);
                        tweetContext.refresher({});
                        tweetContext.setIsTweetTextChanged(true);

                        const newColumns = columns.map(col => {
                          if (col.id === column.id) {
                            return {
                              ...col,
                              items: [postId, ...col.items]
                            };
                          }
                          return col;
                        });
                        await saveAndRefreshKanban(
                          newColumns.filter(col =>
                            col.id !== NO_STATUS_COLUMN.id &&
                            col.id !== SCHEDULED_COLUMN.id
                          )
                        );
                        tweetContext.open();
                      } : undefined}
                      onEdit={() => handleEditColumn(column)}
                      onDelete={() => handleDeleteColumn(column.id)}
                    >
                      <SortableContext items={column.items}>
                        <VStack align="stretch" spacing={4} overflowY="auto" position="relative">
                          {column.items.length === 0 &&
                            column.id === SCHEDULED_COLUMN.id &&
                            placeholder?.columnId !== SCHEDULED_COLUMN.id ? (
                            <Flex direction="column" align="center" justify="center" height="100%" mt={20} >
                              <EmptyScheduleIcon />
                              <Text fontSize="sm" color={placeholderTextColor}>No post scheduled</Text>
                            </Flex>
                          ) : (
                            <>
                              {column.items.map((itemId, index) => (
                                <>
                                  {placeholder?.columnId === column.id && placeholder.index === index && (
                                    <Box
                                      height={`${draggedItemHeight}px`}
                                      borderRadius="md"
                                      border="1px dashed"
                                      borderColor={useColorModeValue(colors.light[column.color].placeholderText, colors.dark[column.color].placeholderText)}
                                      flexShrink={0}
                                    />
                                  )}
                                  <Item
                                    post={column.id === SCHEDULED_COLUMN.id ? scheduledDraftsMap[itemId] : draftsMap[itemId]}
                                    id={itemId}
                                    key={itemId}
                                    account={account}
                                    isComposerActive={tweetContext.id === itemId}
                                    onPreview={() => {
                                      tweetContext.setSelectedTweetPreview(draftsMap[itemId] || scheduledDraftsMap[itemId]);
                                      tweetContext.onOpenTweetPreview();
                                    }}
                                    onDelete={() => {
                                      setPendingDeleteId(itemId);
                                      openDeleteModal();
                                    }}
                                    onEdit={() => {
                                      tweetContext.setTabIndex(0);
                                      tweetContext.newTweet(draftsMap[itemId] || scheduledDraftsMap[itemId], itemId);
                                      tweetContext.refresher({});
                                      tweetContext.setIsTweetTextChanged(true);
                                      tweetContext.open();
                                    }}
                                  />
                                </>
                              ))}
                            </>
                          )}
                          {/* Show placeholder at the end of the column if needed */}
                          {placeholder?.columnId === column.id &&
                            placeholder.index === column.items.length && (
                              <Box
                                height={`${draggedItemHeight}px`}
                                borderRadius="md"
                                border="1px dashed"
                                borderColor={useColorModeValue(colors.light[column.color].placeholderText, colors.dark[column.color].placeholderText)}
                              />
                            )}
                        </VStack>
                      </SortableContext>
                    </Column>
                  ))}
              </Grid>
            </SortableContext>
            <DragOverlay>
              {activeId ? (
                getDraggedItem(activeId) ? (
                  <ItemDragOverlay />
                ) : getDraggedColumn(activeId) ? (
                  <Column
                    id={activeId as string}
                    title={getDraggedColumn(activeId)?.title || ''}
                    color={getDraggedColumn(activeId)?.color || 'grey'}
                    isDraggable={false}
                  >
                    <VStack align="stretch" spacing={4}>
                      {getDraggedColumn(activeId)?.items.map((itemId) => (
                        <Item
                          key={itemId}
                          id={itemId}
                          post={draftsMap[itemId]}
                          account={account}
                          isComposerActive={tweetContext.id === itemId}
                        />
                      ))}
                    </VStack>
                  </Column>
                ) : null
              ) : null}
            </DragOverlay>
          </DndContext>
        </Box>
      </Flex>
    </Box>
  );
}
